import { modals } from "@mantine/modals";
import DataTable from "../components/common/DataTable";
import { Button, Paper, Image, Select, Text, FileButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { Upload } from "lucide-react";
import React from "react";
import useValidate from "../hooks/useValidate";
import {
  addPoster,
  deletePoster,
  getAllMovies,
  getAllSeries,
  getPoster,
  updatePoster,
} from "../utils/api";
import useStore from "../utils/store";


const Poster = () => {
  const user = useStore((state) => state.user);
  const [loading, setLoading] = React.useState(false);
  
  const [notification, setNotification] = React.useState({});
  const [poster, setPoster] = React.useState({});

  const getPosterHandler = async () => {
    if (!user?.getAllPosters) {
      notifications.show({ message: "You don't have permission to view posters", color: "red" });
      return;
    }
    setLoading(true);
    const [success, response] = await getPoster();
    if (success) setPoster(response);
    setLoading(false);
  };

  const addUserNotifications = async () => {
    if (!user?.addPoster) {
      notifications.show({ message: "You don't have permission to add posters", color: "red" });
      return;
    }
    if (!notification?.banner || !notification?.type || !notification?.id)
      return notifications.show({ message: "Please fill all the fields", color: "red" });
    setLoading(true);
    const formData = new FormData();

    formData.append("banner", notification.banner);
    formData.append("type", notification.type);
    formData.append("id", notification.id);
    const [success, response] = poster?.image
      ? await updatePoster(formData)
      : await addPoster(formData);
    if (success) {
      notifications.show({ message: "Poster added successfully", color: "green" });
      getPosterHandler();
    }
    setLoading(false);
  };

  const [movie, setMovie] = React.useState([]);
  const [series, setSeries] = React.useState([]);
  const [documentaries, setDocumentaries] = React.useState([]);
  const [musicVideos, setMusicVideos] = React.useState([]);
  const [shortFilms, setShortFilms] = React.useState([]);

  const getAllMoviesHandler = async () => {
    const [success, data] = await getAllMovies();
    if (success) {
      setMovie(data);
    }
  };

  const getAllSeriesHandler = async () => {
    const [success, data] = await getAllSeries();
    if (success) {
      setSeries(data);
    }
  };

  const getAllDocumentariesHandler = async () => {
    const [success, data] = await getAllMovies(1, 200, "Approved", "Documentary");
    if (success) {
      setDocumentaries(data);
    }
  };

  const getAllMusicVideosHandler = async () => {
    const [success, data] = await getAllMovies(1, 200, "Approved", "Music Video");
    if (success) {
      setMusicVideos(data);
    }
  };

  const getAllShortFilmsHandler = async () => {
    const [success, data] = await getAllMovies(1, 200, "Approved", "Short Film");
    if (success) {
      setShortFilms(data);
    }
  };

  useValidate();

  React.useEffect(() => {
    getPosterHandler();
    getAllMoviesHandler();
    getAllSeriesHandler();
    getAllDocumentariesHandler();
    getAllMusicVideosHandler();
    getAllShortFilmsHandler();
  }, []);

  if (!user?.getAllPosters && !user?.addPoster) {
    return (
      <Text c="red">You don't have permission to view this page</Text>
    );
  }

  return (
    <>
      
      {!poster?.image && user?.addPoster && (
        <Paper withBorder p="md" title="Poster" className="notification_card">
          <div className="notification">
            <Select
              placeholder="Select Type"
              data={[
                { label: "Movie", value: "movie" },
                { label: "Series", value: "series" },
                { label: "Documentary", value: "documentary" },
                { label: "Music Video", value: "musicVideo" },
                { label: "Short Films", value: "shortFilm" },
              ]}
              onChange={(e) => {
                setNotification({ ...notification, type: e, id: null });
              }}
            />

            <Select
              placeholder="Select Content"
              data={
                notification?.type === "movie"
                  ? movie?.data?.map((item) => {
                      return { label: item.name, value: item._id };
                    })
                  : notification?.type === "series"
                  ? series?.data?.map((item) => {
                      return { label: item.name, value: item._id };
                    })
                  : notification?.type === "documentary"
                  ? documentaries?.data?.map((item) => {
                      return { label: item.name, value: item._id };
                    })
                  : notification?.type === "musicVideo"
                  ? musicVideos?.data?.map((item) => {
                      return { label: item.name, value: item._id };
                    })
                  : shortFilms?.data?.map((item) => {
                      return { label: item.name, value: item._id };
                    })
              }
              onChange={(e) => {
                setNotification({ ...notification, id: e });
              }}
            />

            <FileButton
              onChange={(file) => setNotification({ ...notification, banner: file })}
              accept="image/*"
            >
              {(props) => (
                <Button leftSection={<Upload size={16} />} {...props}>
                  {notification.banner ? notification.banner.name : "Upload Image"}
                </Button>
              )}
            </FileButton>
          </div>
          <Button
            onClick={addUserNotifications}
            loading={loading}
            variant="filled"
            style={{ marginTop: "20px" }}
          >
            {poster?.image ? "Update" : "Add"}
          </Button>
        </Paper>
      )}
      <DataTable
        dataSource={poster?.image ? [poster] : []}
        columns={[
          {
            title: "Banner",
            dataIndex: "image",
            key: "image",
            render: (text, record) => (
              <Image
                src={record.image}
                alt="banner"
                style={{ width: "100px", height: "100px" }}
              />
            ),
          },
          {
            title: "Movie/Series Name",
            dataIndex: ["link", "item", "name"],
          },
          {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (text, record) => (
              <>
                {user?.deletePoster && (
                  <Button color="red"
                    onClick={() => {
                      modals.openConfirmModal({
                        title: "Do you want to delete this poster?",
                        onConfirm: async () => {
                          if (!user?.permissions?.includes("delete_poster")) {
                            notifications.show({ message: 
                              "You don't have permission to delete posters"
                            , color: "red" });
                            return;
                          }
                          const [success, response] = await deletePoster();
                          if (success) {
                            notifications.show({ message: "Poster deleted successfully", color: "green" });
                            getPosterHandler();
                          }
                        },
                        onCancel() {},
                      });
                    }}
                  >
                    Delete
                  </Button>
                )}
              </>
            ),
          },
        ]}
      />
    </>
  );
};

export default Poster;
